import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../users/Auth/_services/auth.service';
import { AbilitiesService } from '../abilities/abilities.service';
import { AccountFeaturesService } from '../account-features/account-features.service';
import { StatusPageService } from '../status-page/status-page.service';
import { canAccessStatusHub } from 'src/app/modules/status-page/services/status-page-capabilities.service';
declare let window: any;

@Injectable({
  providedIn: 'root',
})

export class AppInitService {
  private readonly statusPageSubdomainAliasSuffixStorageKey = 'status_page_subdomain_alias_suffix';

  constructor(
    private readonly authService: AuthService,
    private readonly abilitiesService: AbilitiesService,
    private readonly accountFeaturesService: AccountFeaturesService,
    private readonly statusPageService: StatusPageService
  ) { }

  public init() {
    return from(
      fetch('assets/app-config.json').then(function (response) {
        return response.json();
      })
    ).pipe(
      switchMap((config) => {
        window.config = config.apiUrl;
        window.apiV3Url = config.apiV3Url;
        window.env = config.environment;
        window.aiAlertApiUrl = config.aiAlertApiUrl;
        window.aiAlertChatApiUrl = config.aiAlertChatApiUrl;
        window.kb_wp_ApiUrl = config.kb_wp_ApiUrl;
        window.kb_domain_ApiUrl = config.kb_domain_ApiUrl;
        window.adaChatUrl = config.adaChatUrl;
        window.newUiUrl = config.newUiUrl;
        window.uiTransitionEndDate = config.uiTransitionEndDate;
        return new Observable<void>((observer) => {
          this.authService.getUserByToken().pipe(
            switchMap((user) => {
              if (user) {
                const token = this.authService.getToken(); // Access token from AuthService
                if (!token) {
                  return of(null);
                }
                return this.abilitiesService.fetchAbilities(token).pipe(
                  switchMap((abilities) => {
                    // Status-page access comes from auth/me. Load it before the runtime
                    // config so locked accounts do not call /status_pages/config.
                    return this.abilitiesService.fetchAuthMeContext(token).pipe(
                      switchMap((authMeContext) => {
                        return this.loadStatusPageConfigIfNeeded(token, authMeContext).pipe(
                          map(() => abilities)
                        );
                      }),
                      catchError(() => of(abilities))
                    );
                  }),
                  switchMap(() => this.accountFeaturesService.getAppAccountFeatures(token)),
                  map((features) => {
                    if (features) {
                      this.accountFeaturesService.updateAccountFeatures(features);
                    }
                    return features;
                  }),
                  catchError(() => of(null))
                );
              } else {
                return of(null);
              }
            })
          ).subscribe({
            next: () => {
              // Do something if needed
            },
            error: () => {
              observer.next();  // or observer.error(err) if you want to stop bootstrap
              observer.complete();
            },
            complete: () => {
              observer.next();
              observer.complete();
            }
          });
        });
      }));
  }

  /**
   * Loads status-page runtime config only for users who can access Status Hub
   * and only when the subdomain alias suffix is not already cached.
   */
  private loadStatusPageConfigIfNeeded(token: string, authMeContext: any): Observable<any> {
    // Match the header menu unlock rule and trust the cached suffix until logout clears storage.
    if (!this.hasStatusHubAccess(authMeContext) || this.hasCachedStatusPageSubdomainAliasSuffix()) {
      return of(null);
    }

    return this.statusPageService.getStatusPageConfig(token).pipe(
      map((config) => {
        this.storeStatusPageConfig(config);
        return config;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Resolves whether Status Hub is accessible using the same capability rule as the top menu.
   */
  private hasStatusHubAccess(authMeContext: any): boolean {
    // Config-only users still need the alias suffix for list/config screens.
    const caps = authMeContext?.capabilities?.statusPage;
    return canAccessStatusHub(caps);
  }

  /**
   * Returns whether the status-page subdomain alias suffix is already available locally.
   */
  private hasCachedStatusPageSubdomainAliasSuffix(): boolean {
    return !!String(localStorage.getItem(this.statusPageSubdomainAliasSuffixStorageKey) || '').trim();
  }

  /**
   * Stores the normalized status-page subdomain alias suffix, or clears it when absent.
   */
  private storeStatusPageConfig(config: { subdomainAliasSuffix?: string } | null | undefined): void {
    const suffix = String(config?.subdomainAliasSuffix || '').trim().toLowerCase().replace(/^\.+/, '');
    if (suffix) {
      localStorage.setItem(this.statusPageSubdomainAliasSuffixStorageKey, suffix);
    }
  }
}
